import { useCallback, useMemo } from 'react'
import {
  usePrivateProjectNotes,
  usePrivateCalendarNotes,
  useTeamProjectNotes,
  useTeamCalendarNotes,
} from '../../../hooks/useNotesFactory'
import { Note } from '../../../utils/note'
import { FilterConfig, Level } from '../types'
import { applyFilters, extractAndGroupTasksByDate } from '../utils'
import { trackError } from '../../../lib/analytics'
import { useQueryClient } from '@tanstack/react-query'
import {
  cachedNotesContext,
  getNoteFromAnyCache,
} from '../../../contexts/CachedNotes'
import { useAuthenticatedUser } from '../../../providers/UserProvider'
import { AuthType } from '../../../utils/User'
import { useReferences } from '../../../providers/ReferencesContext'
import dayjs from 'dayjs'

export function useFilteredNotes(filters: FilterConfig, taskLimit = 500) {
  // Get query client and user for breadcrumb checking
  const cachedNotesQueryClient = useQueryClient({ context: cachedNotesContext })
  const user = useAuthenticatedUser()

  // Get references map for efficient lookup
  const references = useReferences()

  // Extract teamUserId safely based on authType
  const teamUserId =
    user.authType === AuthType.CLOUDKIT ? undefined : user.teamUserId

  // Fetch notes using existing hooks
  const {
    data: privateProjectNotes,
    isLoading: isPrivateProjectLoading,
    error: privateProjectError,
  } = usePrivateProjectNotes()

  const {
    data: privateCalendarNotes,
    isLoading: isPrivateCalendarLoading,
    error: privateCalendarError,
  } = usePrivateCalendarNotes()

  const {
    data: teamProjectNotes,
    isLoading: isTeamProjectLoading,
    error: teamProjectError,
  } = useTeamProjectNotes()

  const {
    data: teamCalendarNotes,
    isLoading: isTeamCalendarLoading,
    error: teamCalendarError,
  } = useTeamCalendarNotes()

  // Function to check if a note is in an @ folder (like @Templates)
  const isInAtFolder = useCallback(
    (note: Note): boolean => {
      // Check if note itself is an @ folder
      if (note.filename.startsWith('@')) {
        return true
      }

      // Walk up the parent chain to find any @ folder
      let currentNote = note
      const visited = new Set<string>() // Prevent infinite loops

      while (currentNote.parent && !visited.has(currentNote.parent)) {
        visited.add(currentNote.parent)
        const parentNote = getNoteFromAnyCache(
          cachedNotesQueryClient,
          user.userId,
          currentNote.parent
        )

        if (!parentNote) break

        // Check if parent starts with @
        if (parentNote.filename.startsWith('@')) {
          return true
        }

        currentNote = parentNote
      }

      return false
    },
    [cachedNotesQueryClient, user.userId]
  )

  // Helper function to generate all date keys for a date range, so we can query them from the references system
  const getDateKeysInRange = useCallback(
    (dateRange: FilterConfig['dateRange']): string[] => {
      if (!dateRange) return []

      const dateKeys: string[] = []
      const today = dayjs()

      // Always include 'today' as it's a special reference
      dateKeys.push('today')

      // Generate date keys based on the range type
      switch (dateRange.type) {
        case 'thisMonth': {
          const startOfMonth = today.startOf('month')
          const endOfMonth = today.endOf('month')
          let current = startOfMonth
          while (
            current.isBefore(endOfMonth) ||
            current.isSame(endOfMonth, 'day')
          ) {
            dateKeys.push(current.format('YYYY-MM-DD'))
            current = current.add(1, 'day')
          }
          break
        }
        case 'thisWeek': {
          const startOfWeek = today.startOf('week')
          const endOfWeek = today.endOf('week')
          let current = startOfWeek
          while (
            current.isBefore(endOfWeek) ||
            current.isSame(endOfWeek, 'day')
          ) {
            dateKeys.push(current.format('YYYY-MM-DD'))
            current = current.add(1, 'day')
          }
          break
        }
        case 'thisQuarter': {
          const startOfQuarter = today.startOf('quarter')
          const endOfQuarter = today.endOf('quarter')
          let current = startOfQuarter
          while (
            current.isBefore(endOfQuarter) ||
            current.isSame(endOfQuarter, 'day')
          ) {
            dateKeys.push(current.format('YYYY-MM-DD'))
            current = current.add(1, 'day')
          }
          break
        }
        case 'thisYear': {
          const startOfYear = today.startOf('year')
          const endOfYear = today.endOf('year')
          let current = startOfYear
          while (
            current.isBefore(endOfYear) ||
            current.isSame(endOfYear, 'day')
          ) {
            dateKeys.push(current.format('YYYY-MM-DD'))
            current = current.add(1, 'day')
          }
          break
        }
        case 'rolling30Days': {
          const startDate = today.subtract(1, 'day')
          const endDate = today.add(29, 'day')
          let current = startDate
          while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
            dateKeys.push(current.format('YYYY-MM-DD'))
            current = current.add(1, 'day')
          }
          break
        }
        case 'future': {
          // For future, we'll need to check individual scheduled tasks
          // Include next 10 years+ as a reasonable limit
          let current = today.add(1, 'day')
          const endDate = today.add(4650, 'day')
          while (current.isBefore(endDate)) {
            dateKeys.push(current.format('YYYY-MM-DD'))
            current = current.add(1, 'day')
          }
          break
        }
        case 'past': {
          // For past, include previous 10 years+ as a reasonable limit
          let current = today.subtract(4650, 'day')
          const endDate = today.subtract(1, 'day')
          while (current.isBefore(endDate)) {
            dateKeys.push(current.format('YYYY-MM-DD'))
            current = current.add(1, 'day')
          }
          break
        }
      }

      return dateKeys
    },
    []
  )

  // Memoize the filter function to prevent unnecessary recreations
  const filterNote = useCallback(
    (note: Note) => {
      try {
        // First check if note is in an @ folder
        if (isInAtFolder(note)) {
          return false
        }
        return applyFilters(
          note,
          filters,
          cachedNotesQueryClient,
          user.userId,
          teamUserId
        )
      } catch (error) {
        trackError('filterView', `Error filtering note: ${note.filename}`, {
          error: error instanceof Error ? error.message : String(error),
        })
        return false
      }
    },
    [filters, isInAtFolder, cachedNotesQueryClient, user.userId, teamUserId]
  )

  // Apply filters to the notes and generate task groups if needed
  const filteredResult = useMemo(() => {
    try {
      // Collect all notes from different sources and deduplicate
      const notesMap = new Map<string, Note>()

      // Add private project notes
      if (privateProjectNotes?.map) {
        for (const note of privateProjectNotes.map.values()) {
          const key = note.recordName ?? note.filename
          notesMap.set(key, note)
        }
      }

      // Add private calendar notes
      if (privateCalendarNotes?.map) {
        for (const note of privateCalendarNotes.map.values()) {
          const key = note.recordName ?? note.filename
          notesMap.set(key, note)
        }
      }

      // Add team project notes
      if (teamProjectNotes?.map) {
        for (const note of teamProjectNotes.map.values()) {
          const key = note.recordName ?? note.filename
          notesMap.set(key, note)
        }
      }

      // Add team calendar notes
      if (teamCalendarNotes?.map) {
        for (const note of teamCalendarNotes.map.values()) {
          const key = note.recordName ?? note.filename
          notesMap.set(key, note)
        }
      }

      // Convert to array and apply filters
      const allNotes = [...notesMap.values()]
      const baseFilteredNotes = allNotes.filter((note) => filterNote(note))

      // Special handling for TASKS level with date range filter
      const additionalNotes: Note[] = []
      if (filters.level === Level.TASKS && filters.dateRange) {
        // Use references system to find notes that reference dates in the range
        // This includes both wikilinks (like [[2025-01-23]]) and scheduled tasks (like >2025-01-23)
        const dateKeysInRange = getDateKeysInRange(filters.dateRange)
        const additionalNoteRecordNames = new Set<string>()

        for (const dateKey of dateKeysInRange) {
          const referencingNotes = references.get(dateKey)
          if (referencingNotes) {
            for (const [recordName] of referencingNotes) {
              additionalNoteRecordNames.add(recordName)
            }
          }
        }

        // Add all additional notes that were found via references
        for (const recordName of additionalNoteRecordNames) {
          const note = notesMap.get(recordName)
          if (
            note &&
            !baseFilteredNotes.includes(note) &&
            !isInAtFolder(note)
          ) {
            additionalNotes.push(note)
          }
        }
      }

      // Combine base filtered notes with additional notes
      const allFilteredNotes = [...baseFilteredNotes, ...additionalNotes]
      
      // Limit results to 5000 for performance
      const filteredNotes = allFilteredNotes.slice(0, 5000)

      // Generate task groups if level is tasks
      let taskGroups
      let taskLimitReached = false
      let totalTaskCount = 0
      if (filters.level === Level.TASKS) {
        const result = extractAndGroupTasksByDate(filteredNotes, filters, 'asc', taskLimit)
        taskGroups = result.taskGroups
        taskLimitReached = result.limitReached
        totalTaskCount = result.totalTaskCount
      }

      return {
        filteredNotes,
        taskGroups,
        taskLimitReached,
        totalTaskCount,
      }
    } catch (error) {
      trackError('filterView', 'Error filtering notes', {
        error: error instanceof Error ? error.message : String(error),
      })
      return {
        filteredNotes: [],
        taskGroups: undefined,
        taskLimitReached: false,
        totalTaskCount: 0,
      }
    }
  }, [
    privateProjectNotes,
    privateCalendarNotes,
    teamProjectNotes,
    teamCalendarNotes,
    filters,
    filterNote,
    references,
    getDateKeysInRange,
    isInAtFolder,
    taskLimit,
  ])

  // Combine loading states
  const isLoading =
    isPrivateProjectLoading ||
    isPrivateCalendarLoading ||
    isTeamProjectLoading ||
    isTeamCalendarLoading

  // Combine errors - ensure proper typing for Error | undefined
  const error: Error | undefined =
    (privateProjectError as Error | undefined) ??
    (privateCalendarError as Error | undefined) ??
    (teamProjectError as Error | undefined) ??
    (teamCalendarError as Error | undefined) ??
    undefined

  return {
    filteredNotes: filteredResult.filteredNotes,
    taskGroups: filteredResult.taskGroups,
    taskLimitReached: filteredResult.taskLimitReached,
    totalTaskCount: filteredResult.totalTaskCount,
    isLoading,
    error,
  }
}
